/**
 * Routes, the landing redirect, and the first-sign-in gate.
 *
 * `/` sends the user to the first config type their permissions allow, rather
 * than to a fixed screen — someone granted only debtor consent should land on
 * debtor consent, not on a 403.
 *
 * Someone who has never picked a region sees the region picker and nothing
 * else: until they register, the API answers every other call with a 403.
 */

import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { Banner, ErrorBanner, Spinner } from './components/feedback'
import { useSchema } from './api/hooks'
import { ConfigTypePage } from './pages/ConfigTypePage'
import { AuditPage } from './pages/AuditPage'
import { PermissionsPage } from './pages/PermissionsPage'
import { RegionPicker } from './pages/RegionPicker'

function Landing() {
  const { data, isLoading, error } = useSchema()

  if (isLoading) return <div className="page"><Spinner /></div>
  if (error) return <div className="page"><ErrorBanner error={error} /></div>

  const first = data?.config_types[0]
  if (first) return <Navigate to={`/config/${first.key}`} replace />

  // Registered and granted nothing. This is the expected state for a new
  // starter, so it says what to do rather than reading as a failure.
  return (
    <div className="page">
      <Banner tone="info" title="Nothing has been shared with you yet">
        You are set up as {data?.user.role_label ?? 'a user'}. An admin for your region
        grants access one screen at a time on the Access screen — ask yours, and this
        page fills in once they have.
      </Banner>
    </div>
  )
}

function NotFound() {
  const navigate = useNavigate()
  return (
    <div className="page">
      <Banner tone="warn" title="That page does not exist">
        <button type="button" className="btn btn--sm" onClick={() => navigate('/')}>
          Back to configuration
        </button>
      </Banner>
    </div>
  )
}

const signOut = () =>
  void import('./auth/msal').then(({ msalInstance }) =>
    msalInstance.logoutRedirect({ account: msalInstance.getActiveAccount() }),
  )

export function App() {
  const { data } = useSchema()

  if (data && !data.user.registered) return <RegionPicker onSignOut={signOut} />

  return (
    <AppShell onSignOut={signOut}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/config/:type" element={<ConfigTypePage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/access" element={<PermissionsPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  )
}
